import _ from 'lodash';
import {
  INIT_MAIN,
  TOGGLE_OPTION,
  TOGGLE_OPTION_SUCCESS,
  TOGGLE_OPTION_ERROR,
  TOGGLE_OPTION_CONFLICT,
  TOGGLE_OPTION_CONFLICT_RESOLVE,
  TOGGLE_OPTION_CONFLICT_CANCEL,
  UPDATE_CONFIG,
  UPDATE_CONFIG_SUCCESS,
  UPDATE_CONFIG_ERROR,
  UPDATE_POSTAL_CODE,
  START_OVER,
  START_OVER_SUCCESS
} from '../actionTypes';
import { LOCATION_COOKIE } from '../../constants';
import { normalizeStyle } from '../../resources/normalizeHelpers/normalizeHelpers.constant';
import { putCookieObject } from '../../common/cookieHelpers/cookieHelpers';
import {
  clearAllByModel,
  CONFIG,
  getByModelAndKey,
  setByModelAndKey
} from '../../resources/localStorage/localStorage.service';
import { getModelParams } from '../../common/model/utils';
import fetchIncentives from '../../api/fetchIncentives';
import { translate } from '../../common/translate/translations';
import {
  fetchByStyleId,
  fetchByStyleIdAndSerializedState,
  fetchConfig,
  fetchToggleOption
} from '../../api/fetchConfig';

const setConfig = (config, options) => {
  const existing = getByModelAndKey(window.appResources, CONFIG);
  const configToSave = _.assign(
    existing,
    {
      _lastModified: new Date(),
      ss: config.ss,
      styleId: config.styleId,
      bodyStyleTitle: window.appResources.bodyStyleTitle
    },
    options,
    getModelParams()
  );
  setByModelAndKey(window.appResources, CONFIG, configToSave);
};

const createNormalizeConfigWithDisclosures = disclosures => config => {
  config.styles = config.styles.map(style => {
    normalizeStyle(style, disclosures);
    return style;
  });
  return config;
};

const normalizeMatrix = matrix => {
  for (let option of matrix.options) {
    const msrpData = option.msrp;
    option.styleId = `${option.styleId}`;
    option.rearWheelType = `${option.rearWheelType || ''}`;
    const bodyType = option.bodyType || option.bodyTypeId || '';
    option.bodyType = `${bodyType}`;
    delete option.bodyTypeId;
    option.msrp = _.get(msrpData, 'amount.value', 0);
    option.message = _.get(msrpData, 'unknown') || '';
  }
  matrix.options = _.sortBy(matrix.options, 'msrp');
  for (let bodyType of matrix.bodyTypes) {
    for (let key in bodyType) {
      bodyType[key] = `${bodyType[key] || ''}`;
    }
  }
  return matrix;
};

const fetchMatrix = (state, optionalParams = {}) => {
  const { features, api, params } = state;
  const { servicesPath } = api;
  const combinedParams = { ...params, ...optionalParams };
  const {
    bodystyle,
    brand,
    carline,
    country,
    lang,
    modelyear,
    postalCode
  } = combinedParams;
  const matrixUrl = `${servicesPath}/trim-matrix/${lang}/${country}/${brand}/${carline}/${modelyear}/${bodystyle}?postalCode=${postalCode}`;

  return features.enableTrimFiltering
    ? fetch(matrixUrl)
        .then(res => res.json())
        .then(normalizeMatrix)
    : Promise.resolve(null);
};

const createDefaultIncentives = config => {
  const endDate = _.get(config, 'incentives.deadline');
  const disclaimer = endDate
    ? translate('byo.page.general.disclaimer.msrp.expiration.context', {
        endDate
      })
    : translate('byo.page.general.disclaimer.msrp.1');

  return {
    disclaimer: disclaimer,
    value: _.get(config, 'incentives.value'),
    lines: [],
    total: 0,
    simpleDisclaimer: translate('byo.page.general.disclaimer.msrp.simple')
  };
};

const fetchIncentivesForConfig = options => config => {
  const {
    servicesPath,
    modelPath,
    postalCode,
    country,
    forceIncentives = false
  } = options;

  const incentivesPromise =
    forceIncentives || country === 'CA'
      ? fetchIncentives({
          servicesPath,
          modelPath,
          postalCode,
          styleId: config.styleId,
          rpos: config.selections.rpos
        })
      : Promise.resolve(createDefaultIncentives(config));

  return incentivesPromise.then(incentives => ({
    config,
    incentives
  }));
};

const getFullConfig = (params, localState) => {
  const { styleId, ss, rpo, postalCode } = params;
  if (styleId && ss) {
    return fetchByStyleIdAndSerializedState(styleId, ss.replace(/\s/g, '+'), {
      postalCode
    });
  }

  if (styleId) {
    return fetchByStyleId(styleId, {
      postalCode,
      rpos: rpo
    });
  }

  if (localState) {
    return fetchByStyleIdAndSerializedState(localState.styleId, localState.ss, {
      postalCode
    });
  }

  return fetchConfig({ postalCode });
};

export const fetchInitialConfig = (params, onSuccess, onError) => {
  return (dispatch, getState) => {
    const state = getState();
    const { disclosures, appResources } = state;

    const localState = getByModelAndKey(appResources, CONFIG);

    const fullConfigPromise = getFullConfig(params, localState)
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(fetchIncentivesForConfig({ ...state.api, ...params }));

    const matrixPromise = fetchMatrix(state);

    Promise.all([fullConfigPromise, matrixPromise]).then(
      ([{ config, incentives }, matrix]) => {
        setConfig(config);
        dispatch({
          type: INIT_MAIN,
          payload: {
            fullConfig: config,
            incentives,
            matrix
          }
        });
        onSuccess(config);
      },
      err => {
        onError(err);
      }
    );
  };
};

export const toggleOptionConflictResolve = option => {
  return (dispatch, getState) => {
    const { params, conflictConfig: config, api } = getState();

    setConfig(config, { toggled: true });

    fetchIncentivesForConfig({ ...api, ...params })(config).then(
      ({ config, incentives }) => {
        dispatch({
          type: TOGGLE_OPTION_CONFLICT_RESOLVE,
          payload: {
            fullConfig: config,
            option,
            incentives
          }
        });
      }
    );
  };
};

export const toggleOptionConflictCancel = () => ({
  type: TOGGLE_OPTION_CONFLICT_CANCEL
});

export const toggleOption = option => {
  return (dispatch, getState) => {
    const state = getState();
    const { disclosures, conflictConfig, params, api } = state;
    const config = conflictConfig || state.config;

    dispatch({ type: TOGGLE_OPTION });

    fetchToggleOption(config.styleId, config.ss, option.id, {
      postalCode: params.postalCode
    })
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(
        config => {
          if (config.hasConflict) {
            dispatch({
              type: TOGGLE_OPTION_CONFLICT,
              payload: {
                option,
                fullConfig: config
              }
            });
            return;
          }

          return fetchIncentivesForConfig({ ...api, ...params })(config).then(
            ({ config, incentives }) => {
              setConfig(config, { toggled: true });
              dispatch({
                type: TOGGLE_OPTION_SUCCESS,
                payload: {
                  fullConfig: config,
                  option,
                  incentives
                }
              });
            }
          );
        },
        () => {
          dispatch({ type: TOGGLE_OPTION_ERROR });
        }
      );
  };
};

const updateConfig = selections => {
  return (dispatch, getState) => {
    const { params, disclosures, api } = getState();

    dispatch({ type: UPDATE_CONFIG });

    fetchConfig({ postalCode: params.postalCode, ...selections })
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(fetchIncentivesForConfig({ ...api, ...params }))
      .then(
        ({ config, incentives }) => {
          setConfig(config, { toggled: false });
          dispatch({
            type: UPDATE_CONFIG_SUCCESS,
            payload: {
              fullConfig: config,
              incentives
            }
          });
        },
        () => {
          dispatch({ type: UPDATE_CONFIG_ERROR });
        }
      );
  };
};

export const updateConfigWithOption = (type, option) => {
  return (dispatch, getState) => {
    const { config } = getState();
    const existingSelections = config.selections;

    let selections = _.pick(existingSelections, [
      'axleRatio',
      'bodyTypeId',
      'driveType',
      'engine',
      'styleId',
      'transmission'
    ]);
    selections[type] = option.id;

    dispatch(updateConfig(selections));
  };
};

export const updateConfigWithSelections = selections =>
  updateConfig(selections);

export const updateConfigWithStyleIdAndSerializedState = (
  styleId,
  ss,
  options
) => {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_CONFIG });

    const { params, disclosures, api } = getState();

    fetchByStyleIdAndSerializedState(styleId, ss, {
      postalCode: params.postalCode
    })
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(fetchIncentivesForConfig({ ...api, ...params }))
      .then(
        ({ config, incentives }) => {
          setConfig(config, {
            toggled: false,
            ...options
          });
          dispatch({
            type: UPDATE_CONFIG_SUCCESS,
            payload: {
              fullConfig: config,
              incentives,
              wasTrimOptionToggled: _.get(options, 'toggledTrim', false)
            }
          });
        },
        () => {
          dispatch({ type: UPDATE_CONFIG_ERROR });
        }
      );
  };
};

export const updatePostalCode = (postalCode, data, isSummary, cb) => {
  return (dispatch, getState) => {
    const state = getState();
    const { config, disclosures, api } = state;

    putCookieObject(LOCATION_COOKIE, data);

    dispatch({
      type: UPDATE_POSTAL_CODE,
      payload: { postalCode }
    });

    const configAndIncentivesPromise = fetchByStyleIdAndSerializedState(
      config.styleId,
      config.ss,
      { postalCode }
    )
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(config => {
        return fetchIncentivesForConfig({
          ...api,
          ...state.params,
          postalCode,
          forceIncentives: isSummary
        })(config);
      });

    const matrixPromise = fetchMatrix(state, { postalCode });

    Promise.all([configAndIncentivesPromise, matrixPromise]).then(
      ([{ config, incentives }, matrix]) => {
        setConfig(config, { toggled: false });
        dispatch({
          type: UPDATE_CONFIG_SUCCESS,
          payload: {
            fullConfig: config,
            incentives,
            matrix
          }
        });

        if (cb) {
          cb();
        }
      }
    );
  };
};

export const startOver = () => {
  return (dispatch, getState) => {
    const { disclosures, params, appResources, api } = getState();

    clearAllByModel(appResources);
    dispatch({ type: START_OVER });

    fetchConfig({ postalCode: params.postalCode })
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(fetchIncentivesForConfig({ ...api, ...params }))
      .then(({ config, incentives }) => {
        setConfig(config);
        dispatch({
          type: START_OVER_SUCCESS,
          payload: {
            fullConfig: config,
            incentives
          }
        });
      });
  };
};
